"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import Navbar from "../components/Navbar"
import { useAuth } from "../context/AuthContext"
import "./YourProducts.css"
import productIcon from "../assets/product_icon.png"
import axios from "axios"

interface Product {
  id: number
  name: string
  price: number
  description: string
  images: { image_url: string }[]
  is_sold: boolean
}

const YourProducts: React.FC = () => {
  const { user } = useAuth()
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<string>("all")
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [productToDelete, setProductToDelete] = useState<number | null>(null)

  const fetchUserProducts = async () => {
    try {
      const response = await axios.get(`https://new-thriftshop.onrender.com/api/products/my-products`, {
        withCredentials: false,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      setProducts(response.data.products)
    } catch (error) {
      console.error("Error fetching user products:", error)
    } finally {
      setLoading(false)
    }
  }

  const confirmDelete = (productId: number) => {
    setProductToDelete(productId)
    setShowDeleteModal(true)
  }

  const executeDelete = async () => {
    if (!productToDelete) return
    try {
      await axios.delete(`https://new-thriftshop.onrender.com/api/products/${productToDelete}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      setProducts(products.filter((p) => p.id !== productToDelete))
      setShowDeleteModal(false)
      setProductToDelete(null)
    } catch (error) {
      console.error("Error deleting product:", error)
    }
  }

  const handleMarkAsSold = async (id: number, current: boolean) => {
    const token = localStorage.getItem("token")
    await axios.patch(
      `https://new-thriftshop.onrender.com/api/products/${id}/sold`,
      { is_sold: !current },
      { headers: { Authorization: `Bearer ${token}` } },
    )
    await fetchUserProducts()
  }

  const filteredProducts = products.filter((product) => {
    if (filter === "all") return true
    if (filter === "available") return !product.is_sold
    if (filter === "sold") return product.is_sold
    return true
  })

  const filterOptions = [
    { key: "all", label: "All Products" },
    { key: "available", label: "Available" },
    { key: "sold", label: "Sold" },
  ]

  useEffect(() => {
    fetchUserProducts()
  }, [])

  return (
    <>
      <Navbar />
      <div className="products-container-your">
        <div className="container-your">
          <div className="products-header-your">
            <h1 className="products-title-your">Your Products</h1>
            <p className="products-subtitle-your">Manage your product listings and track your sales</p>
          </div>

          <div className="products-filters-your">
            {filterOptions.map((option) => (
              <button
                key={option.key}
                className={`filter-btn-your ${filter === option.key ? "active" : ""}`}
                onClick={() => setFilter(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="products-conten-your">
            {loading ? (
              <div className="loading-state-your">
                <div className="loading-spinner-your"></div>
                <p>Loading your products...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="empty-state-your">
                <div className="empty-icon-your">📦</div>
                <h3>No products found</h3>
                <p>{filter === "all" ? "You haven't listed any products yet." : `No ${filter} products found.`}</p>
                <Link to="/publish-product" className="add-product-btn-empty-your">
                  Add Your First Product
                </Link>
              </div>
            ) : (
              <div className="products-grid-your">
                {filteredProducts.map((product) => (
                  <div key={product.id} className="product-card-your">
                    <div className="product-image-container-your">
                      <img
                        src={product.images?.length > 0 ? product.images[0].image_url : "/placeholder.png"}
                        alt={product.name}
                        className="product-image-your"
                      />
                      <div className={`product-status-your ${product.is_sold ? "status-sold" : "status-available"}`}>
                        {product.is_sold ? "Sold" : "Available"}
                      </div>
                    </div>

                    <div className="product-details-your">
                      <div className="product-header-your">
                        <div className="product-info-your">
                          <span className="product-id-your">Product #{product.id}</span>
                          <h3 className="product-name-your">{product.name}</h3>
                        </div>
                        <div className="product-price-your">₱{product.price.toFixed(2)}</div>
                      </div>

                      <div className="product-description-your">
                        {product.description.length > 120
                          ? `${product.description.substring(0, 120)}...`
                          : product.description}
                      </div>
                    </div>

                    <div className="product-actions-your">
                      <button className="action-btn-your delete-btn-your" onClick={() => confirmDelete(product.id)}>
                        <span className="btn-icon-your">🗑️</span>
                        Delete
                      </button>
                      <button
                        className="action-btn-your toggle-btn-your"
                        onClick={() => handleMarkAsSold(product.id, product.is_sold)}
                      >
                        <span className="btn-icon-your">{product.is_sold ? "📦" : "✅"}</span>
                        {product.is_sold ? "Mark Available" : "Mark as Sold"}
                      </button>
                      <Link to={`/manage-product/${product.id}`} className="action-btn-your manage-btn-your">
                        <span className="btn-icon-your">⚙️</span>
                        Manage
                      </Link>
                    </div>
                  </div>
                ))}

                <div className="add-product-card-your">
                  <div className="add-product-content-your">
                    <div className="add-icon-your">
                      <img src={productIcon || "/placeholder.svg"} alt="Add Product" />
                    </div>
                    <h3>Add New Product</h3>
                    <p>Click here to list a new product for sale</p>
                    <Link to="/publish-product" className="action-btn-your add-btn-your">
                      <span className="btn-icon-your">➕</span>
                      Add Product
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div className="modal-overlay-your">
          <div className="confirm-modal-your">
            <div className="warning-icon-your">⚠️</div>
            <h3>Delete Product</h3>
            <p>Are you sure you want to delete this product? This action cannot be undone.</p>
            <div className="confirm-actions-your">
              <button className="action-btn-your confirm-delete-btn-your" onClick={executeDelete}>
                <span className="btn-icon-your">🗑️</span>
                Delete
              </button>
              <button className="action-btn-your cancel-btn-your" onClick={() => setShowDeleteModal(false)}>
                <span className="btn-icon-your">❌</span>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default YourProducts
